import { Dispatch, SetStateAction } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { getLPFilterRequest, LPFRProps } from "./filter-obj";
import {
  getListadoPersonas,
  GetListadoPersonasProps,
  GetListadoPersonasResponse,
} from "@/services/persons.service";
import { AxiosError } from "axios";
import { handlePermissionError } from "@/utils/permission-check";
import { NavigateFunction } from "react-router-dom";
import { toaster } from "@/components/ui/toaster";

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  submitFilters: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = ({
  getValues,
  setValue,
  submitFilters,
  setFiltersApplied,
}: ClearFiltersHandlerProps) => {
  setValue("first_name", "");
  setValue("last_name", "");
  setValue("cuit", "");
  setValue("dni", "");
  setFiltersApplied(false);

  submitFilters?.(getValues());
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setPage: Dispatch<SetStateAction<number>>;
  setCurrentFilters: Dispatch<SetStateAction<{}>>;
  fetchPersons: (filters?: any) => Promise<void>;
  event: FieldValues;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const submitFiltersHandler = async ({
  event,
  fetchPersons,
  setCurrentFilters,
  setFiltersApplied,
  setOpenFilters,
  setPage,
  navigate,
  fetchPermissions,
}: SubmitFiltersHandlerProps) => {
  try {
    setPage(1);
    setFiltersApplied(true);

    const filterObj: Partial<GetListadoPersonasProps> = getLPFilterRequest(
      event as LPFRProps,
    );

    setCurrentFilters(filterObj);

    if (!Object.keys(filterObj)?.length) setFiltersApplied(false);
    await fetchPersons(filterObj);

    setOpenFilters(false);
  } catch (error) {
    const errorObj = error as AxiosError<{
      message: string;
      tiposFaltantes?: string[];
    }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudieron aplicar los filtros.",
      });
    }
    throw error;
  }
};

interface FetchPersonsHandlerProps {
  setLoadingPersons: Dispatch<SetStateAction<boolean>>;
  setPersons: Dispatch<SetStateAction<GetListadoPersonasResponse | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  showLoad?: boolean;
  filters?: GetListadoPersonasProps;
}

export const fetchPersonsHandler = async ({
  setLoadingPersons,
  setPersons,
  navigate,
  fetchPermissions,
  showLoad = true,
  filters,
}: FetchPersonsHandlerProps) => {
  try {
    if (showLoad) setLoadingPersons(true);

    const response = await getListadoPersonas({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setPersons(response);

    setLoadingPersons(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cargar las personas.",
      });
    }
  } finally {
    setLoadingPersons(false);
  }
};
